import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle2, Circle, Loader2, ShieldAlert, Monitor } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => iso ? new Date(iso).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
export const fmtMinutes = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m || 0}m`);
const pageLabel = (p) => ({ pos: 'POS', dashboard: 'Dashboard', inventory: 'Inventory', reports: 'Reports', customers: 'Customers', 'customer-orders': 'Customer Orders', 'smart-po': 'Smart PO', protocols: 'Protocols', staff: 'Staff', settings: 'Settings' }[p] || p.replace(/-/g, ' '));

// Compact engagement strip for a business card in the Admin Console
export const UsageBadge = ({ usage, onOpen, testId }) => {
  if (!usage) return null;
  const recent = usage.last_seen && (Date.now() - new Date(usage.last_seen).getTime()) < 24 * 3600 * 1000;
  return (
    <button type="button" onClick={onOpen} className="w-full text-left mt-3 rounded-lg border px-3 py-2 hover:bg-gray-50 transition-colors" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700">
        {usage.has_used ? (
          <span className={`inline-flex items-center gap-1 font-medium ${recent ? 'text-emerald-700' : 'text-gray-700'}`} data-testid="usage-state">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {recent ? 'Using it — active today' : 'Has used it'}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-gray-400" data-testid="usage-state"><Circle className="w-4 h-4" /> Not logged in yet</span>
        )}
        {usage.has_used && (
          <>
            <span><b>{usage.logins_total}</b> login{usage.logins_total === 1 ? '' : 's'}</span>
            <span><b>{fmtMinutes(usage.active_minutes)}</b> in the app</span>
            <span><b>{usage.pages_viewed}</b> pages · {usage.days_used} day{usage.days_used === 1 ? '' : 's'}</span>
            {usage.top_pages?.length > 0 && <span className="text-gray-500">Most used: {usage.top_pages.slice(0, 3).map(t => pageLabel(t.page)).join(', ')}</span>}
            <span className="text-gray-500">Last seen {when(usage.last_seen || usage.last_login_at)}{usage.last_login_who ? ` · ${usage.last_login_who}` : ''}</span>
          </>
        )}
        {usage.failed_logins > 0 && <span className="inline-flex items-center gap-1 text-amber-700"><ShieldAlert className="w-3.5 h-3.5" /> {usage.failed_logins} failed attempt{usage.failed_logins === 1 ? '' : 's'}</span>}
        <span className="ml-auto text-[#1B5E20] underline">Login activity</span>
      </div>
    </button>
  );
};

export default function BusinessActivityDialog({ business, onClose }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!business) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/platform/businesses/${business.id}/activity`, { headers: getAuthHeader() })
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [business, getAuthHeader]);

  const s = data?.summary;
  return (
    <Dialog open={!!business} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="business-activity-dialog">
        <DialogHeader><DialogTitle>Login activity — {business?.name}</DialogTitle></DialogHeader>
        {loading && <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {s && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="activity-summary">
            <Tile label="Logins" value={s.logins_total} sub={s.failed_logins ? `${s.failed_logins} failed` : 'no failed attempts'} />
            <Tile label="Time in the app" value={fmtMinutes(s.active_minutes)} sub={`${s.days_used} day${s.days_used === 1 ? '' : 's'} used`} />
            <Tile label="Pages viewed" value={s.pages_viewed} sub={s.top_pages?.map(t => pageLabel(t.page)).slice(0, 3).join(', ') || '—'} />
            <Tile label="Last seen" value={s.last_seen ? when(s.last_seen) : '—'} sub={s.last_login_device || ''} />
          </div>
        )}
        {data && (
          <>
            <h3 className="text-sm font-semibold text-gray-800 mt-4">Day by day</h3>
            {data.days.length === 0 ? <p className="text-xs text-gray-500">No activity recorded yet.</p> : (
              <div className="divide-y border rounded-lg" data-testid="activity-days">
                {data.days.map(d => (
                  <div key={`${d.day}-${d.who}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-xs">
                    <span className="font-medium text-gray-800 w-24">{new Date(d.day).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })}</span>
                    <span className="text-gray-600 w-40 truncate">{d.who}</span>
                    <span><b>{fmtMinutes(d.minutes)}</b> active</span>
                    <span>{d.views || 0} pages</span>
                    <span className="text-gray-500 flex-1">{(d.top_pages || []).map(([p, n]) => `${pageLabel(p)} ×${n}`).join(' · ')}</span>
                  </div>
                ))}
              </div>
            )}
            <h3 className="text-sm font-semibold text-gray-800 mt-4">Sign-ins</h3>
            {data.logins.length === 0 ? <p className="text-xs text-gray-500">Nobody has signed in yet.</p> : (
              <div className="divide-y border rounded-lg" data-testid="activity-logins">
                {data.logins.map(l => (
                  <div key={l.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs" data-testid={`login-event-${l.id}`}>
                    <span className="text-gray-500 w-32">{when(l.at)}</span>
                    <span className="font-medium text-gray-800 w-44 truncate">{l.who}</span>
                    <Badge variant="outline" className="text-[10px]">{l.kind === 'staff_pin' ? 'staff PIN' : 'account'}</Badge>
                    {l.success ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px]">signed in</Badge>
                      : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">failed · {l.reason}</Badge>}
                    <span className="inline-flex items-center gap-1 text-gray-500"><Monitor className="w-3 h-3" /> {l.device}</span>
                    <span className="text-gray-400 ml-auto">{l.ip}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Tile = ({ label, value, sub }) => (
  <div className="rounded-lg border p-3">
    <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
    <p className="text-lg font-semibold text-gray-900">{value}</p>
    {sub && <p className="text-[11px] text-gray-500 truncate">{sub}</p>}
  </div>
);
